import { getScopedDomain, getActiveTenant, ALL } from '../lib/tenantScope';
import { useState, useRef, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, X, Info } from 'lucide-react';
import { api, getToken } from '../lib/api';
import VoiceGreetingPanel from '../components/VoiceGreetingPanel';
import ScopeBanner from '../components/ScopeBanner';
import { describeError } from '../lib/portalUx';

const SUBPAGES = [
  { to: 'maintenance', label: 'Maintenance' },
  { to: 'options',     label: 'Options' },
  { to: 'dial-codes',  label: 'Dial Codes' },
  { to: 'parking',     label: 'Call Parking' },
  { to: 'emergency',   label: 'Emergency' },
  { to: 'conferencing',label: 'Conferencing' },
  { to: 'email',       label: 'Email' },
  { to: 'moh',         label: 'Music on Hold' },
  { to: 'voicemail',   label: 'Voicemail' },
  { to: 'alerts',      label: 'Alerts' },
  { to: 'storage',     label: 'Storage' },
];

// SystemPages renders only the matched sub-route. The Layout's secondary pane
// already surfaces the System sub-nav so we don't render an internal one here.
export default function SystemPages() {
  return (
    <Routes>
      <Route index element={<Navigate to="dial-codes" replace />} />
      <Route path="dial-codes" element={<DialCodes />} />
      <Route path="options"     element={<Options />} />
      <Route path="parking"     element={<Parking />} />
      <Route path="emergency"   element={<Emergency />} />
      <Route path="conferencing"element={<Conferencing />} />
      <Route path="email"       element={<Email />} />
      <Route path="moh"         element={<MoH />} />
      <Route path="voicemail"   element={<VM />} />
      <Route path="alerts"      element={<Alerts />} />
      <Route path="maintenance" element={<Maintenance />} />
      <Route path="storage"     element={<Storage />} />
    </Routes>
  );
}

function useTenant() {
  const me = api.me();
  return getScopedDomain(me?.domain_uuid) as string;
}

function SettingsForm<T>({ load, save, render, title }: {
  load: (d: string) => Promise<T>;
  save: (d: string, body: any) => Promise<any>;
  render: (cur: T, set: (next: T) => void) => JSX.Element;
  title: string;
}) {
  const d = useTenant();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: [title, d], queryFn: () => load(d), enabled: !!d });
  const [edited, setEdited] = useState<T | null>(null);
  const saveM = useMutation({
    mutationFn: (next: T) => save(d, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: [title, d] }),
  });

  const cur = (edited ?? data) as T;
  // A failed load (incl. 403 for a non-system admin) used to leave "Loading…" up forever.
  if (error) return <LoadError title={title} error={error} />;
  if (!d) return <LoadError title={title} error={new Error('No department selected.')} />;
  if (isLoading || !cur) return <div>Loading…</div>;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <ScopeBanner what={`The ${title} page`} />
      <div className="card space-y-3">
        {render(cur, setEdited)}
        <div className="flex gap-2 pt-2">
          <button onClick={() => saveM.mutate(cur)} disabled={saveM.isPending} className="btn-primary">Save</button>
          {saveM.isPending && <span className="text-textSecondary text-sm self-center">saving…</span>}
          {saveM.isSuccess && <span className="text-success text-sm self-center">saved</span>}
          {saveM.isError && <span className="text-danger text-sm self-center">{describeError(saveM.error)?.message || 'error saving'}</span>}
        </div>
      </div>
    </div>
  );
}

// Error state for the System sub-pages. 403 = the caller is not a system administrator.
function LoadError({ title, error }: { title: string; error: unknown }) {
  const e = describeError(error);
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <div className="card p-4 border border-danger/50 text-sm space-y-1">
        <div className="font-medium text-danger">
          {e?.status === 403 ? 'This page is for system administrators only.' : 'This page could not be loaded.'}
        </div>
        {e?.message && <div className="text-textSecondary">{e.message}</div>}
      </div>
    </div>
  );
}

// Friendly labels for the editable Dial Codes page (keys are the dial_codes emitter's ACTIONS).
const DIAL_CODE_LABELS: Record<string, { label: string; hint?: string }> = {
  park:             { label: 'Park a call', hint: 'Not advertised to users: parking by dialing the orbit number (e.g. 6000) is the supported way today.' },
  pickup_parked:    { label: 'Retrieve a parked call (code + orbit)', hint: 'Known broken right now (retrieve by dialing the orbit number instead). Not shown to users until fixed.' },
  pickup:           { label: 'Pick up a ringing phone (code + extension)' },
  change_profile:   { label: 'Change status' },
  voicemail_of_ext: { label: 'Voicemail (code = own box, code + ext = leave a message)' },
  queue_login:      { label: 'Queue log in' },
  queue_logout:     { label: 'Queue log out' },
  paging:           { label: 'Paging' },
  billing_code:     { label: 'Billing code prefix' },
  block_cid:        { label: 'Block caller ID', hint: 'Behaviour unverified; not shown to users.' },
  hotdesking:       { label: 'Hot desk log in (code + extension)' },
  maid_codes:       { label: 'Room status (hotel housekeeping)' },
};

function DialCodes() {
  return (
    <SettingsForm
      title="Dial Codes"
      load={api.system.dialCodes}
      save={api.system.saveDialCodes}
      render={(cur: any, set) => (
        <div className="space-y-3">
          <p className="text-sm text-textSecondary">
            Users and department admins see a read-only version of these on the <a href="/feature-codes" className="text-primary underline">Feature Codes</a> page.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(cur).map(([k, v]) => {
              const meta = DIAL_CODE_LABELS[k];
              return (
                <div key={k}>
                  <label className="block text-sm text-textSecondary mb-1">
                    {meta ? meta.label : k.replace(/_/g, ' ')} <span className="font-mono text-[10px] opacity-60">({k})</span>
                  </label>
                  <input className="input font-mono" value={String(v ?? '')} onChange={e => set({ ...cur, [k]: e.target.value })} />
                  {meta?.hint && <p className="text-xs text-warning mt-1">{meta.hint}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    />
  );
}

function Options() {
  return (
    <SettingsForm
      title="Options"
      load={api.system.options}
      save={api.system.saveOptions}
      render={(cur: any, set) => (
        <div className="space-y-3">
          <Field label="Language" value={cur.language} onChange={v => set({ ...cur, language: v })} />
          <Field label="Time Zone" value={cur.time_zone} onChange={v => set({ ...cur, time_zone: v })} />
          <Field label="Custom Status 1" value={cur.custom_status_1} onChange={v => set({ ...cur, custom_status_1: v })} />
          <Field label="Custom Status 2" value={cur.custom_status_2} onChange={v => set({ ...cur, custom_status_2: v })} />
          <h3 className="text-sm font-semibold mt-4">System Flags</h3>
          {Object.entries(cur.flags || {}).map(([k, v]) => (
            <label key={k} className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!v} onChange={e => set({ ...cur, flags: { ...cur.flags, [k]: e.target.checked } })} />
              {k.replace(/_/g, ' ')}
            </label>
          ))}
        </div>
      )}
    />
  );
}

// Call Parking = a GLOBAL pool of parking spots, each assigned to at most one
// department (the 3CX model). Set the total count, then assign each spot's owner.
// Users only ever see their own department's spots (BLF picker filters by owner),
// and each department's dialplan only emits its own numbers — so no two offices
// share an orbit and the BLF lamp can't cross-light (#55/#56).
function Parking() {
  const qc = useQueryClient();
  const pool = useQuery({ queryKey: ['parking-pool'], queryFn: () => api.system.parkingPool() });
  const depts = useQuery({ queryKey: ['tenants-for-parking'], queryFn: () => api.tenants.list() });
  const [count, setCount] = useState<number | null>(null);
  const [timeoutS, setTimeoutS] = useState<number | null>(null);
  const [assign, setAssign] = useState<Record<string, string | null>>({});
  // Follow the top-bar "Viewing" department selector. `scoped` = the selected
  // department's domain_uuid, or null for "All departments".
  const [activeTenant, setActive] = useState<string>(getActiveTenant());
  useEffect(() => {
    const h = () => { setActive(getActiveTenant()); setTimeoutS(null); setCount(null); setAssign({}); };
    window.addEventListener('stratus-tenant-changed', h);
    return () => window.removeEventListener('stratus-tenant-changed', h);
  }, []);
  const scoped = activeTenant !== ALL ? activeTenant : null;

  const saveM = useMutation({
    mutationFn: () => {
      const base = pool.data!.base;
      const dflt = pool.data!.timeout_seconds;
      const curTimeouts = pool.data!.timeouts || {};
      const timeouts: Record<string, number> = {};
      let c: number, t: number;
      if (scoped) {
        c = pool.data!.count;                 // dept view never resizes the global pool
        t = dflt;                             // ...nor changes the default
        // Only write an override when the admin actually set a value (or one already
        // exists) — otherwise leave the dept following the pool default. Saving an
        // untouched dept with no override must NOT pin a phantom override equal to the
        // current default (which would stop tracking future default changes). 0 clears.
        if (timeoutS != null || curTimeouts[scoped] != null) {
          timeouts[scoped] = (timeoutS ?? curTimeouts[scoped]) as number;
        }
      } else {
        c = count ?? pool.data!.count;
        t = timeoutS ?? dflt;                 // "All" edits the default
      }
      const hi = base + c - 1;
      const merged: Record<string, string | null> = {};
      for (const s of (pool.data!.spots || [])) merged[s.code] = s.domain_uuid;
      for (const [k, v] of Object.entries(assign)) merged[k] = v;
      const assignments = Object.entries(merged)
        .filter(([code]) => Number(code) >= base && Number(code) <= hi)
        .map(([code, domain_uuid]) => ({ code, domain_uuid }));
      return api.system.saveParkingPool({ base, count: c, timeout_seconds: t, timeouts, assignments });
    },
    onSuccess: () => { setAssign({}); setCount(null); setTimeoutS(null); qc.invalidateQueries({ queryKey: ['parking-pool'] }); },
  });

  if (pool.error) return <LoadError title="Call Parking" error={pool.error} />;
  if (pool.isLoading || !pool.data) return <div>Loading…</div>;
  const base = pool.data.base;
  const c = count ?? pool.data.count;
  const dflt = pool.data.timeout_seconds;
  const curTimeouts = pool.data.timeouts || {};
  const deptList = depts.data || [];
  const deptName = (uuid: string | null) => {
    const d = deptList.find((x: any) => x.domain_uuid === uuid);
    return d ? (d.domain_description || d.domain_name) : uuid;
  };
  const ownerFor = (code: string): string | null =>
    code in assign ? assign[code] : (pool.data!.spots.find(x => x.code === code)?.domain_uuid ?? null);
  const rows = Array.from({ length: Math.min(Math.max(c, 0), 999) }, (_, i) => String(base + i));
  // Dept view: only that dept's spots + still-unassigned spots (claimable) + anything
  // edited this session (so a row doesn't vanish mid-change).
  const visibleRows = scoped
    ? rows.filter(code => { const o = ownerFor(code); return o === scoped || o === null || (code in assign); })
    : rows;
  const timeoutVal = timeoutS ?? (scoped ? (curTimeouts[scoped] ?? dflt) : dflt);
  const usingDefault = scoped && timeoutS == null && curTimeouts[scoped] == null;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Call Parking{scoped ? ` — ${deptName(scoped)}` : ''}</h1>
      <div className="card space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {scoped ? (
            <div>
              <label className="block text-sm text-textSecondary mb-1">Total parking spots</label>
              <input className="input opacity-60" value={String(c)} disabled readOnly />
              <p className="text-xs text-textSecondary mt-1">Pool size is set under "All departments".</p>
            </div>
          ) : (
            <NumField label="Total parking spots" value={c} onChange={(v: number) => setCount(Math.max(0, Math.min(v, 999)))} />
          )}
          <NumField
            label={scoped ? `Park timeout for ${deptName(scoped)} (seconds)` : 'Default park timeout (seconds)'}
            value={timeoutVal}
            onChange={(v: number) => setTimeoutS(v)}
          />
        </div>
        <p className="text-sm text-textSecondary">
          {scoped
            ? <>Showing <b>{deptName(scoped)}</b>'s parking spots plus unassigned spots you can claim. The timeout above is this department's park-recall time{usingDefault ? ` (currently the pool default of ${dflt}s — enter a value to override, or 0 to revert).` : ' (set 0 to revert to the pool default).'}</>
            : <>Assign each spot to the department that owns it. A spot can belong to only one department, and each user only sees their own department's spots on their BLF keys. The timeout above is the default recall time for all departments without their own override. Set to 0 to disable recall — parked calls stay parked until manually retrieved.</>
}
        </p>
        {/* Let the spot list grow down the page instead of scrolling inside a
            fixed 384px box (#340): a short pool shows every row with no thin
            inner scrollbar, and a large pool flows down the page. The header
            still sticks — now to page scroll — via position: sticky. */}
        <div className="border rounded">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-black/40 backdrop-blur">
              <tr><th className="text-left p-2 w-32">Spot</th><th className="text-left p-2">Owning department</th></tr>
            </thead>
            <tbody>
              {visibleRows.map(code => (
                <tr key={code} className="border-t border-white/10">
                  <td className="p-2 font-mono">{code}</td>
                  <td className="p-2">
                    <select className="input" value={ownerFor(code) || ''}
                      onChange={e => setAssign(a => ({ ...a, [code]: e.target.value || null }))}>
                      <option value="">— Unassigned —</option>
                      {deptList.map((d: any) => (
                        <option key={d.domain_uuid} value={d.domain_uuid}>{d.domain_description || d.domain_name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {!visibleRows.length && (
                <tr><td colSpan={2} className="p-3 text-textSecondary">No spots owned by this department yet. Switch to "All departments" to assign spots.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={() => saveM.mutate()} className="btn-primary">Save</button>
          {saveM.isPending && <span className="text-textSecondary text-sm self-center">saving…</span>}
          {saveM.isSuccess && <span className="text-success text-sm self-center">saved</span>}
          {saveM.isError && <span className="text-red-400 text-sm self-center">error saving</span>}
        </div>
      </div>
    </div>
  );
}

function Emergency() {
  // Emergency is a list — render as JSON for now (full editor is a future task)
  return (
    <SettingsForm title="Emergency Rules" load={api.system.emergency} save={api.system.saveEmergency}
      render={(cur: any, set) => (
        <div>
          <textarea className="input font-mono text-xs h-96" value={JSON.stringify(cur || [], null, 2)}
            onChange={e => { try { set(JSON.parse(e.target.value)); } catch {} }} />
        </div>
      )}
    />
  );
}

function Conferencing() {
  return (
    <SettingsForm title="Conferencing" load={api.system.conferencing} save={api.system.saveConferencing}
      render={(cur: any, set) => (
        <div className="space-y-3">
          <Field label="Audio extension" value={cur.audio_extension} onChange={v => set({ ...cur, audio_extension: v })} />
          <Field label="On-hold prompt" value={cur.on_hold_prompt} onChange={v => set({ ...cur, on_hold_prompt: v })} />
          <Field label="PIN" value={cur.pin || ''} onChange={v => set({ ...cur, pin: v })} />
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={!!cur.force_pin} onChange={e => set({ ...cur, force_pin: e.target.checked })} />
            Force organizer to enter PIN
          </label>
        </div>
      )}
    />
  );
}

function Email() {
  return (
    <SettingsForm title="Email" load={api.system.email} save={api.system.saveEmail}
      render={(cur: any, set) => (
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={!!cur.use_default} onChange={e => set({ ...cur, use_default: e.target.checked })} />
            Use Stratus default SMTP
          </label>
          <Field label="SMTP host" value={cur.smtp_host} onChange={v => set({ ...cur, smtp_host: v })} />
          <NumField label="SMTP port" value={cur.smtp_port} onChange={v => set({ ...cur, smtp_port: v })} />
          <Field label="SMTP user" value={cur.smtp_user} onChange={v => set({ ...cur, smtp_user: v })} />
          <Field label="SMTP password" value="" onChange={v => set({ ...cur, smtp_password: v })} placeholder="(unchanged)" type="password" />
          <Field label="From" value={cur.smtp_from} onChange={v => set({ ...cur, smtp_from: v })} />
        </div>
      )}
    />
  );
}

function MoH() {
  const d = useTenant();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const cfgQ = useQuery({ queryKey: ['MoH', d], queryFn: () => api.system.moh(d) });
  const filesQ = useQuery({ queryKey: ['MoH-files'], queryFn: () => api.system.mohFiles() });
  // Same capability probe the generator uses — the "Generate with Navi" button shows
  // when speech (Orpheus) OR instrumental music (lofi library) generation is possible.
  const ttsQ = useQuery<any>({
    queryKey: ['tts-voices'],
    queryFn: async () => { const r = await fetch('/api/tts/voices', { headers: { Authorization: `Bearer ${getToken()}` } }); return r.ok ? r.json() : { enabled: false, music: false }; },
  });
  const canGenerate = !!(ttsQ.data?.enabled || ttsQ.data?.music);
  const [genOpen, setGenOpen] = useState(false);
  const [edited, setEdited] = useState<any>(null);
  const cur: any = edited ?? cfgQ.data;
  const files: any[] = filesQ.data || [];

  const saveM = useMutation({
    mutationFn: (next: any) => api.system.saveMoh(d, next),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['MoH', d] }); setEdited(null); },
  });
  const uploadM = useMutation({
    mutationFn: (file: File) => api.system.uploadMohFile(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['MoH-files'] }),
  });
  const delM = useMutation({
    mutationFn: (name: string) => api.system.deleteMohFile(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['MoH-files'] }),
  });
  // System default hold music (#1229) — the fleet-wide fallback every held/parked
  // caller falls through to when neither the user nor the tenant set a track.
  const sysRef = useRef<HTMLInputElement>(null);
  const sysQ = useQuery({ queryKey: ['MoH-system-default'], queryFn: () => api.system.mohSystemDefault() });
  const sysM = useMutation({
    mutationFn: (file: File) => api.system.uploadMohSystemDefault(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['MoH-system-default'] }),
  });

  if (cfgQ.error) return <LoadError title="Music on Hold" error={cfgQ.error} />;
  if (cfgQ.isLoading || !cur) return <div>Loading…</div>;

  const tracks: string[] = (cur.slots || []).map((s: any) => s?.filename).filter(Boolean);
  const setTracks = (list: string[]) => {
    const slots = list.map((f) => ({ filename: f }));
    while (slots.length < 10) slots.push({} as any);
    setEdited({ ...cur, slots });
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Music on Hold</h1>
      <ScopeBanner what="The Music on Hold playlist" />

      {/* System default — bottom of the hold-music cascade */}
      <div className="card space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">System default hold music</h2>
          <div>
            <input ref={sysRef} type="file" accept=".wav,audio/wav,audio/x-wav" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) sysM.mutate(f); e.target.value = ''; }} />
            <button className="btn-ghost" disabled={sysM.isPending} onClick={() => sysRef.current?.click()}>
              {sysM.isPending ? 'Uploading…' : 'Replace default'}
            </button>
          </div>
        </div>
        <p className="text-textSecondary text-sm">
          The fleet-wide fallback: this plays for any held or parked caller when neither the user nor their department has chosen a track — so a caller on hold is <strong>never</strong> met with silence.
        </p>
        <div className="text-sm">
          {sysQ.data?.present
            ? <span className="text-success">✓ A system default track is set{sysQ.data.size ? ` (${Math.round((sysQ.data.size || 0) / 1024)} KB)` : ''}.</span>
            : <span className="text-danger">No system default track set — upload one so held callers always hear music.</span>}
        </div>
        {sysM.isError && <div className="text-danger text-sm">{(sysM.error as any)?.message || 'Upload failed'}</div>}
        {sysM.isSuccess && <div className="text-success text-sm">Saved — system default updated.</div>}
      </div>

      {/* Library */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Music library</h2>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".wav,audio/wav,audio/x-wav" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadM.mutate(f); e.target.value = ''; }} />
            {canGenerate && (
              <button className="btn-ghost inline-flex items-center gap-1.5" onClick={() => setGenOpen(true)}>
                <Sparkles size={14} /> Generate with Navi
              </button>
            )}
            <button className="btn-primary" disabled={uploadM.isPending} onClick={() => fileRef.current?.click()}>
              {uploadM.isPending ? 'Uploading…' : 'Upload WAV'}
            </button>
          </div>
        </div>
        <p className="text-textSecondary text-xs flex items-start gap-1.5 bg-elevated border border-border rounded-md px-3 py-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Just upload a WAV — Stratus <strong>automatically converts it to the correct hold-music format</strong> (8&nbsp;kHz mono), so any sample rate, stereo or bit-depth is fine. Up to 50&nbsp;MB.</span>
        </p>
        {uploadM.isError && <div className="text-danger text-sm">{(uploadM.error as any)?.message || 'Upload failed'}</div>}
        {files.length === 0 ? (
          <p className="text-textSecondary text-sm">No music uploaded yet — click <strong>Upload WAV</strong> to add your first track (it’s auto-converted to the right format).</p>
        ) : (
          <div className="space-y-2">
            {files.map((f) => (
              <div key={f.name} className="flex items-center gap-3 border-b border-border pb-2">
                <span className="flex-1 text-sm">{f.name} <span className="text-textSecondary text-xs">({Math.round((f.size || 0) / 1024)} KB)</span></span>
                <audio controls preload="none" src={api.system.mohBlobUrl(f.name)} className="h-8" style={{ maxWidth: 240 }} />
                <button className="btn-ghost text-danger text-sm" disabled={delM.isPending}
                  onClick={() => { if (confirm(`Delete "${f.name}" from the library?`)) delM.mutate(f.name); }}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-tenant playlist */}
      <div className="card space-y-3">
        <h2 className="font-semibold">Hold-music playlist</h2>
        <p className="text-textSecondary text-sm">Tracks selected here play when callers in this department are placed on hold.</p>
        {tracks.length === 0 && <p className="text-textSecondary text-sm">No tracks selected — callers hear the system default until you add one.</p>}
        <div className="space-y-2">
          {tracks.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <select className="input flex-1" value={t}
                onChange={(e) => { const next = [...tracks]; next[i] = e.target.value; setTracks(next.filter(Boolean)); }}>
                {t && files.every((f) => f.name !== t) && <option value={t}>{t} (missing)</option>}
                {files.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
              </select>
              <button className="btn-ghost text-danger px-2" title="Remove" onClick={() => setTracks(tracks.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
        </div>
        {files.length > 0 && tracks.length < 10 && (
          <button className="btn-ghost text-sm" onClick={() => setTracks([...tracks, files[0].name])}>+ Add track</button>
        )}
        <label className="flex items-center gap-2 text-sm pt-1">
          <input type="checkbox" checked={!!cur.randomize} onChange={(e) => setEdited({ ...cur, randomize: e.target.checked })} />
          Shuffle tracks (randomize playback order)
        </label>
        <div className="flex gap-2 pt-2">
          <button className="btn-primary" disabled={saveM.isPending} onClick={() => saveM.mutate(cur)}>Save</button>
          {saveM.isPending && <span className="text-textSecondary text-sm self-center">saving…</span>}
          {saveM.isSuccess && <span className="text-success text-sm self-center">saved — hold music updated</span>}
          {saveM.isError && <span className="text-danger text-sm self-center">{(saveM.error as any)?.message}</span>}
        </div>
      </div>

      {genOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setGenOpen(false)}>
          <div className="card max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><Sparkles size={16} /> Generate hold music with Navi</h3>
              <button type="button" className="btn-ghost p-1" onClick={() => setGenOpen(false)}><X size={16} /></button>
            </div>
            <VoiceGreetingPanel saveMode="moh"
              onSaved={() => { qc.invalidateQueries({ queryKey: ['MoH-files'] }); setGenOpen(false); }} />
          </div>
        </div>
      )}
    </div>
  );
}

function VM() {
  return (
    <SettingsForm title="Voicemail" load={api.system.voicemail} save={api.system.saveVoicemail}
      render={(cur: any, set) => (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Menu extension" value={cur.menu_extension} onChange={v => set({ ...cur, menu_extension: v })} />
          <NumField label="Quota (GB)" value={cur.quota_gb} onChange={v => set({ ...cur, quota_gb: v })} />
          <NumField label="Min seconds" value={cur.do_not_save_under_seconds} onChange={v => set({ ...cur, do_not_save_under_seconds: v })} />
          <NumField label="Auto-delete after days" value={cur.auto_delete_older_than_days || 0} onChange={v => set({ ...cur, auto_delete_older_than_days: v })} />
        </div>
      )}
    />
  );
}

// 3-tab Alerts page mirroring 3CX shape exactly:
//   1. System Alerts          — host-level resource thresholds
//   2. Stratus Alerts         — PBX-specific (trunk down, cert expiring, etc.)
//   3. Notification Delivery  — email recipients + severity filter + digest
function Alerts() {
  const d = useTenant();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['Alerts', d], queryFn: () => api.system.alerts(d) });
  const [edited, setEdited] = useState<any>(null);
  const cur = (edited ?? data) as any;
  const saveM = useMutation({
    mutationFn: (next: any) => api.system.saveAlerts(d, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['Alerts', d] }),
  });
  const [tab, setTab] = useState<'system' | 'stratus' | 'delivery'>('system');

  if (error) return <LoadError title="Alerts" error={error} />;
  if (isLoading || !cur) return <div>Loading…</div>;
  const set = (patch: any) => setEdited({ ...cur, ...patch });

  const enabled = (k: string, fallback = true) => cur[`${k}_enabled`] ?? fallback;
  const setEnabled = (k: string, v: boolean) => set({ [`${k}_enabled`]: v });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Alerts</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => saveM.mutate(cur)}>
          {saveM.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      <ScopeBanner what="The Alerts page" />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {([
          ['system',   'System Alerts'],
          ['stratus',  'Stratus Alerts'],
          ['delivery', 'Notification Delivery'],
        ] as const).map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
                  className={`px-4 py-2 text-sm whitespace-nowrap ${
                    tab === v ? 'border-b-2 border-primary text-primary'
                              : 'text-textSecondary hover:text-textPrimary'
                  }`}>{l}</button>
        ))}
      </div>

      {tab === 'system' && (
        <div className="card p-4 space-y-4">
          <p className="text-sm text-textSecondary">Configure alerts for critical system resource utilization and health issues.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <ThresholdRow label="CPU usage exceeds" unit="%"
                          enabled={enabled('cpu')} onEnabled={v => setEnabled('cpu', v)}
                          value={cur.cpu_pct_threshold ?? 90} onChange={v => set({ cpu_pct_threshold: v })} />
            <ThresholdRow label="Outbound network usage exceeds" unit="Mbps"
                          enabled={enabled('net_out')} onEnabled={v => setEnabled('net_out', v)}
                          value={cur.net_out_mbps_threshold ?? 500} onChange={v => set({ net_out_mbps_threshold: v })} />
            <ThresholdRow label="Physical Memory (RAM) usage exceeds" unit="%"
                          enabled={enabled('ram')} onEnabled={v => setEnabled('ram', v)}
                          value={cur.ram_pct_threshold ?? 90} onChange={v => set({ ram_pct_threshold: v })} />
            <ThresholdRow label="Inbound network usage exceeds" unit="Mbps"
                          enabled={enabled('net_in')} onEnabled={v => setEnabled('net_in', v)}
                          value={cur.net_in_mbps_threshold ?? 500} onChange={v => set({ net_in_mbps_threshold: v })} />
            <ThresholdRow label="Virtual Memory (SWAP) usage exceeds" unit="%"
                          enabled={enabled('swap')} onEnabled={v => setEnabled('swap', v)}
                          value={cur.swap_pct_threshold ?? 90} onChange={v => set({ swap_pct_threshold: v })} />
            <ThresholdRow label="System clock drifts more than" unit="ms"
                          enabled={enabled('clock')} onEnabled={v => setEnabled('clock', v)}
                          value={cur.clock_drift_ms_threshold ?? 5000} onChange={v => set({ clock_drift_ms_threshold: v })} />
            <ThresholdRow label="Disk Usage exceeds" unit="%"
                          enabled={enabled('disk')} onEnabled={v => setEnabled('disk', v)}
                          value={cur.disk_pct_threshold ?? 90} onChange={v => set({ disk_pct_threshold: v })} />
            <BoolRow label="Automatically restart Stratus services if they crash"
                     value={!!cur.auto_restart_services} onChange={v => set({ auto_restart_services: v })} />
            <BoolRow label="Any critical Stratus service stops unexpectedly"
                     value={cur.alert_service_stop_enabled ?? true} onChange={v => set({ alert_service_stop_enabled: v })} />
          </div>
        </div>
      )}

      {tab === 'stratus' && (
        <div className="card p-4 space-y-4">
          <p className="text-sm text-textSecondary">PBX-specific alerts. These fire when telephony services degrade independent of host resource use.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <BoolRow label="Trunk goes offline (OPTIONS health-check failure)"
                     value={cur.alert_trunk_down ?? true} onChange={v => set({ alert_trunk_down: v })} />
            <ThresholdRow label="Trunk failure threshold (consecutive misses)" unit=""
                          enabled={true} onEnabled={() => {}}
                          value={cur.trunk_fail_threshold ?? 3} onChange={v => set({ trunk_fail_threshold: v })} />
            <BoolRow label="TLS certificate expiring within X days"
                     value={cur.alert_cert_expiring ?? true} onChange={v => set({ alert_cert_expiring: v })} />
            <ThresholdRow label="Cert expiry warning window" unit="days"
                          enabled={true} onEnabled={() => {}}
                          value={cur.cert_expiry_warning_days ?? 30} onChange={v => set({ cert_expiry_warning_days: v })} />
            <BoolRow label="Extension deregistered (offline) too long"
                     value={cur.alert_ext_offline ?? true} onChange={v => set({ alert_ext_offline: v })} />
            <ThresholdRow label="Extension offline threshold" unit="min"
                          enabled={true} onEnabled={() => {}}
                          value={cur.ext_offline_minutes ?? 15} onChange={v => set({ ext_offline_minutes: v })} />
            <BoolRow label="Backup job failed"
                     value={cur.alert_backup_failed ?? true} onChange={v => set({ alert_backup_failed: v })} />
            <BoolRow label="Phone firmware advisory (known-bad version detected)"
                     value={cur.alert_firmware_advisory ?? true} onChange={v => set({ alert_firmware_advisory: v })} />
            <BoolRow label="SBC node offline (heartbeat lost)"
                     value={cur.alert_sbc_offline ?? true} onChange={v => set({ alert_sbc_offline: v })} />
            <ThresholdRow label="Call quality MOS drops below" unit="MOS"
                          enabled={cur.alert_mos_low_enabled ?? true} onEnabled={v => set({ alert_mos_low_enabled: v })}
                          value={cur.mos_low_threshold ?? 3.5} onChange={v => set({ mos_low_threshold: v })} />
            <ThresholdRow label="Active calls exceed" unit="concurrent"
                          enabled={cur.alert_concurrent_exceed_enabled ?? false} onEnabled={v => set({ alert_concurrent_exceed_enabled: v })}
                          value={cur.concurrent_calls_threshold ?? 100} onChange={v => set({ concurrent_calls_threshold: v })} />
            <BoolRow label="Failed login burst (anti-hacking trigger)"
                     value={cur.alert_failed_login_burst ?? true} onChange={v => set({ alert_failed_login_burst: v })} />
            <BoolRow label="Disk write errors / I/O failures"
                     value={cur.alert_disk_io ?? true} onChange={v => set({ alert_disk_io: v })} />
          </div>
        </div>
      )}

      {tab === 'delivery' && (
        <div className="card p-4 space-y-4">
          <p className="text-sm text-textSecondary">Where alert emails get delivered, plus severity gating and daily digest options.</p>
          <Field label="Recipients (comma-separated emails)"
                 value={cur.notify_recipients || ''}
                 onChange={v => set({ notify_recipients: v })}
                 placeholder="ops@yourco.com, oncall@yourco.com" />
          <Field label="From address (defaults to system smtp_from)"
                 value={cur.notify_from || ''}
                 onChange={v => set({ notify_from: v })}
                 placeholder="alerts@yourco.com" />
          <div>
            <label className="block text-sm text-textSecondary mb-1">Minimum severity to deliver</label>
            <select className="input" value={cur.notify_min_severity || 'warn'}
                    onChange={e => set({ notify_min_severity: e.target.value })}>
              <option value="info">Info (everything)</option>
              <option value="warn">Warning + Error + Critical</option>
              <option value="error">Error + Critical only</option>
              <option value="critical">Critical only</option>
            </select>
          </div>
          <BoolRow label="Send daily digest summary even if no alerts fire"
                   value={!!cur.notify_daily_digest} onChange={v => set({ notify_daily_digest: v })} />
          {cur.notify_daily_digest && (
            <Field label="Digest delivery time (HH:MM, server local)"
                   value={cur.notify_digest_time || '07:00'}
                   onChange={v => set({ notify_digest_time: v })} />
          )}
          <BoolRow label="Throttle duplicate alerts (suppress repeats within window)"
                   value={cur.notify_throttle ?? true} onChange={v => set({ notify_throttle: v })} />
          {(cur.notify_throttle ?? true) && (
            <ThresholdRow label="Throttle window" unit="min"
                          enabled={true} onEnabled={() => {}}
                          value={cur.notify_throttle_minutes ?? 15} onChange={v => set({ notify_throttle_minutes: v })} />
          )}
          <BoolRow label="Also POST alerts to a webhook"
                   value={!!cur.notify_webhook_enabled} onChange={v => set({ notify_webhook_enabled: v })} />
          {cur.notify_webhook_enabled && (
            <Field label="Webhook URL" value={cur.notify_webhook_url || ''}
                   onChange={v => set({ notify_webhook_url: v })}
                   placeholder="https://hooks.slack.com/services/…" />
          )}
        </div>
      )}
    </div>
  );
}

function ThresholdRow({ label, unit, enabled, onEnabled, value, onChange }: {
  label: string; unit: string;
  enabled: boolean; onEnabled: (v: boolean) => void;
  value: number; onChange: (v: number) => void;
}) {
  return (
    <div className="grid grid-cols-[auto_1fr_70px_auto] items-center gap-2 text-sm">
      <input type="checkbox" checked={enabled} onChange={e => onEnabled(e.target.checked)} />
      <span>{label}</span>
      <input className="input text-right" type="number" value={value}
             onChange={e => onChange(parseFloat(e.target.value) || 0)} disabled={!enabled} />
      <span className="text-textSecondary text-xs">{unit}</span>
    </div>
  );
}

function BoolRow({ label, value, onChange }: {
  label: string; value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm md:col-span-2">
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function Maintenance() {
  const purge = useMutation({ mutationFn: ({ type, days }: { type: string; days?: number }) => api.system.purge(type, days) });
  const TYPES = ['call_log','chat_log','event_log','pbx_logs'];
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Maintenance</h1>
      <div className="card space-y-3">
        <p className="text-sm text-textSecondary">Manage and delete various types of data stored by your Stratus system.</p>
        <table className="table">
          <thead><tr><th>Category</th><th>Action</th></tr></thead>
          <tbody>
            {TYPES.map(t => (
              <tr key={t}>
                <td>{t.replace(/_/g, ' ')}</td>
                <td>
                  <button onClick={() => purge.mutate({ type: t })} className="btn-primary">Purge</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }: any) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <input className="input" type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
function NumField({ label, value, onChange }: any) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <input className="input" type="number" value={value ?? 0} onChange={e => onChange(parseInt(e.target.value, 10) || 0)} />
    </div>
  );
}

// ─── Storage (FTP archive destination) ────────────────────────────────────────
// NOT AVAILABLE (portal-phones-ux). This page used to call GET/PUT /api/system/storage/:d, which
// has no backend, so it rendered "Loading..." forever. The similar /api/system-pages/storage route
// stores config where the archive worker (api/lib/storage_archive.js, not scheduled) never reads
// it, so wiring the form to it would only give false confidence that archiving runs. Until a real
// backend + scheduled worker exist, say so plainly instead of showing a form that does nothing.
function Storage() {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Storage</h1>
      <div className="card p-4 space-y-2 text-sm">
        <div className="font-medium">External archive storage is not available yet.</div>
        <p className="text-textSecondary">
          Stratus does not currently archive call logs, recordings, voicemails or chat logs to an
          FTP/SFTP destination, so there is nothing to configure here. Recording retention is set
          under <a href="/recordings" className="text-primary underline">Recordings</a> (Settings), and
          database backups and their remote destinations are managed on the{' '}
          <a href="/backup" className="text-primary underline">Backup</a> page.
        </p>
      </div>
    </div>
  );
}
